import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert, Tooltip } from 'antd';
import DataTable from 'components/shared/DataTable';
import FilterSearchBar from 'components/shared/FilterSearchBar';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import useVerifyAccess from 'hooks/useVerifyAccess';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { showToast } from 'utils/helpers';
import { getErrorMessage } from 'utils/index';
import { getWarrantyTypes, saveWarrantyType } from 'services/warranty-management.service';
import WarrantyTypeForm from './WarrantyTypeForm';
import WarrantyCommonWorkflowForm from './WarrantyCommonWorkflowForm';
import WarrantyTypeDeleteForm from './WarrantyTypeDeleteForm';
import './warrantyTypes.scss';

const DEFAULT_FILTER = { search: '', is_priority: -1, is_active: -1 };
const PAGE_SIZE = 25;
const SummaryText = ({ children }) => <Tooltip title={children}>
  <span className='bw_warranty_type_summary'>{children || '—'}</span>
</Tooltip>;

const WarrantyTypePage = () => {
  const { verifyPermission } = useVerifyAccess();
  const [scope, setScope] = useState(getPortalScope);
  const [data, setData] = useState({ items: [], periods: [] });
  const [editor, setEditor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [commonEditor,setCommonEditor] = useState(false);
  const [params, setParams] = useState(DEFAULT_FILTER);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sequence = useRef(0);
  const filters = useForm({ defaultValues: DEFAULT_FILTER });
  const canAdd = verifyPermission('MD_WARRANTYTYPE_ADD', false, { requireAssigned: true });
  const canEdit = verifyPermission('MD_WARRANTYTYPE_EDIT', false, { requireAssigned: true });

  const load = useCallback(async () => {
    const current = ++sequence.current;
    setError('');
    if (!scope.company_id) { setData({ items: [], periods: [] }); setLoading(false); return; }
    setLoading(true);
    try {
      const result = await getWarrantyTypes({ company_id: scope.company_id, search: params.search });
      if (current === sequence.current) setData(result);
    } catch (loadError) {
      if (current === sequence.current) {
        setData({ items: [], periods: [] });
        setError(getErrorMessage(loadError));
      }
    } finally { if (current === sequence.current) setLoading(false); }
  }, [scope, params]);
  useEffect(() => { load(); return () => { sequence.current += 1; }; }, [load]);
  useEffect(() => {
    const changed = () => {
      sequence.current += 1;
      setScope(getPortalScope()); setEditor(null); setDeleting(null); setCommonEditor(false); setData({ items: [], periods: [] }); setPage(1);
    };
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, changed);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, changed);
  }, []);

  const items = useMemo(() => data.items.filter(item =>
    (Number(params.is_priority) === -1 || Number(item.is_priority) === Number(params.is_priority))
    && (Number(params.is_active) === -1 || Number(item.is_active) === Number(params.is_active))), [data.items, params]);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const submitFilter = (values) => { setPage(1); setParams({ ...DEFAULT_FILTER, ...values, search: values.search?.trim() || '' }); };
  const clearFilter = () => { filters.reset(DEFAULT_FILTER); submitFilter(DEFAULT_FILTER); };

  const save = async (values) => {
    if (busy) return;
    setBusy(true);
    try {
      await saveWarrantyType(editor.item?.warranty_type_id, {
        ...values, company_id: scope.company_id, expected_version: editor.item?.version_no,
      });
      showToast.success('Đã lưu hình thức bảo hành.'); setEditor(null); await load();
    } catch (saveError) { showToast.error(getErrorMessage(saveError)); }
    finally { setBusy(false); }
  };

  const columns = useMemo(() => [
    { header: 'STT', accessor: 'row_number', style: { width: 55 }, classNameBody: 'bw_text_center' },
    { header: 'Mã hình thức', accessor: 'type_code', style: { width: 190 }, formatter: item =>
      <button type='button' className='bw_warranty_type_link' onClick={() => setEditor({ item, readOnly: true })}>{item.type_code}</button> },
    { header: 'Tên hình thức', accessor: 'type_name', style: { width: 300 }, formatter: item => <div className='bw_warranty_type_name'><i style={{ background: /^#[a-f0-9]{6}$/i.test(item.color || '') ? item.color : '#64748B' }} /><SummaryText>{item.type_name}</SummaryText></div> },
    { header: 'Các bước xử lý', accessor: 'workflow_steps', style: { width: 330 }, formatter: item => <div className='bw_warranty_type_step_summary'>
      {item.workflow_steps?.length ? <><strong>{item.workflow_steps.length} bước{!item.workflow_ready ? ' · Ngừng sử dụng' : ''}</strong><div>{item.workflow_steps.slice(0, 3).map(step => <span key={step.code}><i style={{ background: /^#[a-f0-9]{6}$/i.test(step.color || '') ? step.color : '#64748B' }} />{step.short_name || step.name}</span>)}{item.workflow_steps.length > 3 && <span>+{item.workflow_steps.length - 3}</span>}</div></> : 'Chưa khai báo bước xử lý'}
    </div> },
    { header: 'Ưu tiên', accessor: 'is_priority', style: { width: 130 }, classNameBody: 'bw_text_center', formatter: item => item.is_priority ? <span className='bw_label_outline bw_label_outline_warning'>Ưu tiên xử lý</span> : 'Bình thường' },
    { header: 'Ghi chú', accessor: 'description', style: { width: 250 }, formatter: item => <SummaryText>{item.description}</SummaryText> },
    { header: 'Trạng thái', accessor: 'is_active', style: { width: 145 }, classNameBody: 'bw_text_center', formatter: item =>
      <span className={`bw_label_outline bw_label_outline_${item.is_active ? 'success' : 'danger'}`}>
        {item.is_active ? 'Đang sử dụng' : 'Ngừng sử dụng'}
      </span> },
  ], []);
  const actions = [
    ...(canAdd && scope.company_id ? [{ globalAction: true, icon: 'fi fi-rr-plus', type: 'success', content: 'Thêm hình thức', permission: 'MD_WARRANTYTYPE_ADD', requireAssigned: true, onClick: () => setEditor({ item: null, readOnly: false }) }] : []),
    { icon: 'fi fi-rr-eye', color: 'green', title: 'Chi tiết hình thức', permission: 'MD_WARRANTYTYPE_VIEW', onClick: item => setEditor({ item, readOnly: true }) },
    ...(canEdit ? [{ icon: 'fi fi-rr-edit', color: 'blue', title: 'Chỉnh sửa hình thức', permission: 'MD_WARRANTYTYPE_EDIT', requireAssigned: true, onClick: item => setEditor({ item, readOnly: false }) }] : []),
    ...(canEdit ? [{ icon: 'fi fi-rr-trash', color: 'red', title: 'Xóa hình thức', permission: 'MD_WARRANTYTYPE_EDIT', requireAssigned: true, onClick: item => setDeleting(item) }] : []),
  ];

  return <div className='bw_main_wrapp bw_warranty_types_page'>
    {scope.company_id && <div className='bw_warranty_common_link'><div><strong>Khai báo quy trình theo hình thức</strong><p>Mỗi hình thức bên dưới có bước xử lý, tác vụ và đường chuyển riêng. Quy trình tham chiếu chỉ dùng khi phiếu chưa có quy trình riêng.</p></div><button type='button' onClick={()=>setCommonEditor(true)}>{canEdit?'Khai báo quy trình tham chiếu':'Xem quy trình tham chiếu'}</button></div>}
    <FormProvider {...filters}>
      <FilterSearchBar title='Tìm kiếm' colSize={4} onSubmit={submitFilter} onClear={clearFilter} actions={[
        { title: 'Từ khóa', component: <FormInput field='search' aria-label='Từ khóa' placeholder='Nhập mã hoặc tên hình thức' /> },
        { title: 'Ưu tiên', component: <FormSelect field='is_priority' aria-label='Lọc ưu tiên' list={[{ value: -1, label: 'Tất cả' }, { value: 1, label: 'Ưu tiên xử lý' }, { value: 0, label: 'Bình thường' }]} allowClear={false} /> },
        { title: 'Trạng thái', component: <FormSelect field='is_active' aria-label='Lọc trạng thái' list={[{ value: -1, label: 'Tất cả' }, { value: 1, label: 'Đang sử dụng' }, { value: 0, label: 'Ngừng sử dụng' }]} allowClear={false} /> },
      ]} />
    </FormProvider>
    {!scope.company_id ? <Alert type='info' showIcon message='Chọn công ty để xem và khai báo hình thức bảo hành.' /> : <>
      {error && <Alert type='error' showIcon message={error} />}
      <DataTable loading={loading} title={<strong>Danh sách hình thức bảo hành ({items.length})</strong>}
        columns={columns} data={items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((item, index) => ({ ...item, row_number: (currentPage - 1) * PAGE_SIZE + index + 1 }))}
        actions={actions} noSelect fieldCheck='warranty_type_id' page={currentPage} totalPages={totalPages}
        totalItems={items.length} itemsPerPage={PAGE_SIZE} onChangePage={setPage} columnPreferenceKey='warranty-types' />
    </>}
    {editor && <WarrantyTypeForm item={editor.item} readOnly={editor.readOnly} workflows={data.workflows} routes={data.routes}
      stepCatalog={data.step_catalog || { items: data.steps || [] }} processingLocations={data.processing_locations || []} companyId={scope.company_id}
      busy={busy} onSave={save} onClose={() => setEditor(null)} />}
    {commonEditor && <WarrantyCommonWorkflowForm companyId={scope.company_id} readOnly={!canEdit} onClose={()=>setCommonEditor(false)} />}
    {deleting && <WarrantyTypeDeleteForm item={deleting} companyId={scope.company_id} onClose={() => setDeleting(null)}
      onDeleted={async () => { setDeleting(null); await load(); }} />}
  </div>;
};
export default WarrantyTypePage;
